import {
  estimateBreakdown,
  estimateCharge,
  formatDay,
  formatKr,
  hasPriceInfo,
  pricingOn,
  pricingTerms,
  type RoomPricing,
} from "./pricing";

export interface BookingCostSummaryProps {
  /** The room's ``pricing`` from the API. Renders nothing for a free room. */
  pricing: RoomPricing | null | undefined;
  /** The slot about to be booked (ISO). */
  start: string;
  end: string;
  /** Tighter type for small popovers (the timeline's BookingDialog). */
  compact?: boolean;
}

/** What a paid booking will cost, shown BEFORE the member confirms: the estimated amount for this slot,
 *  how it is worked out, and the room's terms (late cancellation, no-show, invoicing). Nothing for a free
 *  room; "Free" for a slot before the room's price starts. */
export function BookingCostSummary({ pricing, start, end, compact }: BookingCostSummaryProps) {
  if (!hasPriceInfo(pricing)) return null;
  const applying = pricingOn(pricing, start);
  const estimate = estimateCharge(pricing, start, end);
  const color = estimate ? "orange" : "teal";
  const small = compact ? 11 : 12;

  let headline: string;
  let sub: string | null;
  if (estimate) {
    headline = `${estimate.upTo ? "Up to " : ""}${formatKr(estimate.amount)} kr`;
    sub = `${estimateBreakdown(estimate)}, excl. VAT`;
  } else if (applying && !applying.paid && applying.upcoming) {
    headline = "Free";
    sub = `Free to book until ${formatDay(applying.upcoming.starts_on)}`;
  } else if (applying?.paid && applying.charge_mode === "none") {
    headline = "Free to book";
    sub = null;
  } else {
    headline = applying?.label ?? "";
    sub = null;
  }

  // The headline already says it's free; don't repeat that as a term.
  const terms = pricingTerms(applying).filter((t) => !(headline.startsWith("Free") && t.startsWith("Free to book")));

  return (
    <div
      data-testid="booking-cost-summary"
      style={{
        borderRadius: 8,
        padding: compact ? "8px 10px" : "10px 12px",
        // A neutral surface with a coloured frame keeps the dimmed breakdown readable in both schemes
        // (a filled orange-light tint made it low-contrast on dark).
        background: "var(--mantine-color-default)",
        border: `1px solid var(--mantine-color-${color}-text)`,
        borderLeftWidth: 4,
        color: "var(--mantine-color-text)",
      }}
    >
      <div
        style={{
          fontSize: small - 1,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: `var(--mantine-color-${color}-text)`,
        }}
      >
        {estimate?.upTo ? "Estimated cost (maximum)" : "Estimated cost"}
      </div>
      <div
        style={{
          fontSize: compact ? 18 : 22,
          fontWeight: 700,
          lineHeight: 1.2,
          marginTop: 2,
          color: `var(--mantine-color-${color}-text)`,
        }}
      >
        {headline}
      </div>
      {sub && <div style={{ fontSize: small, color: "var(--mantine-color-dimmed)", marginTop: 1 }}>{sub}</div>}
      {terms.length > 0 && (
        <ul style={{ margin: "6px 0 0", paddingLeft: 16, fontSize: small, lineHeight: 1.4 }}>
          {terms.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
